import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";
import { Sparkles, Send, Plus, MessageSquare, ClipboardList, Loader2 } from "lucide-react";
import { toast } from "sonner";

const MODELS = [
  { v: "claude-sonnet-4-5", label: "Claude Sonnet 4.5", short: "Claude" },
  { v: "gpt-5.2", label: "GPT-5.2", short: "GPT" },
];

const SUGGESTIONS = [
  "What are the top 3 deductions I'm missing based on my data?",
  "Build me a 12-month plan to pay off my highest-APR debt.",
  "How can I extend my runway by 6 months?",
  "Should I switch from LLC to S-Corp for tax savings?",
];

function newId() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export default function AIAdvisor() {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(() => newId());
  const [messages, setMessages] = useState([]);
  const [model, setModel] = useState("claude-sonnet-4-5");
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const autoSentRef = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [savingPlanFor, setSavingPlanFor] = useState(null);
  const nav = useNavigate();

  const savePlan = async (assistantMessageId) => {
    if (!assistantMessageId) return;
    setSavingPlanFor(assistantMessageId);
    try {
      const { data } = await api.post(
        "/plans/from-message",
        { message_id: assistantMessageId },
        { timeout: 90000 },
      );
      toast.success(`Plan saved · ${data.actions.length} actions`);
      nav(`/app/plan/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingPlanFor(null);
    }
  };

  const loadSessions = async () => {
    const { data } = await api.get("/ai/sessions");
    setSessions(data);
  };

  const pickSession = async (id) => {
    setSessionId(id);
    const { data } = await api.get(`/ai/messages/${id}`);
    setMessages(data);
  };

  const newChat = () => {
    setSessionId(newId());
    setMessages([]);
  };

  useEffect(() => { loadSessions(); /* eslint-disable-next-line */ }, []);

  // Auto-send when the page is opened with ?q=... (deep-link from metric tooltips)
  useEffect(() => {
    if (autoSentRef.current) return;
    const q = searchParams.get("q");
    if (!q || !sessionId || streaming) return;
    const src = searchParams.get("src") || "deep-link";
    autoSentRef.current = true;
    setSearchParams({}, { replace: true });
    send(q, src);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, sessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text, src) => {
    if (!text.trim() || streaming) return;
    const userMsg = { id: newId(), role: "user", content: text, created_at: new Date().toISOString() };
    const aiMsg = { id: newId(), role: "assistant", content: "", model, created_at: new Date().toISOString() };
    setMessages((m) => [...m, userMsg, aiMsg]);
    setInput("");
    setStreaming(true);

    try {
      const res = await fetch(`${API_BASE}/ai/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId, message: text, model, source: src || null }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          try {
            const payload = JSON.parse(line.slice(5).trim());
            if (payload.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + payload.delta };
                return copy;
              });
            }
          } catch {
            // Skip malformed SSE frames — stream may include heartbeats or partial JSON we can safely ignore.
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], content: `Error: ${e.message}` };
        return copy;
      });
    } finally {
      setStreaming(false);
      loadSessions();
      // Refetch messages so the assistant message id matches the backend record
      // (the Save-as-plan button needs the persisted id).
      try {
        const { data } = await api.get(`/ai/messages/${sessionId}`);
        setMessages(data);
      } catch (e) {
        // Refetch is best-effort — streamed content is already rendered, so a fetch failure
        // only means the assistant message id won't be replaced with the persisted one.
        console.warn("Post-stream message refetch failed:", e?.message);
      }
    }
  };

  return (
    <div className="flex h-full" data-testid="advisor-page">
      <aside className="hidden md:flex flex-col w-64 shrink-0 border-r border-border bg-white">
        <div className="p-4 border-b border-border">
          <button onClick={newChat} className="w-full btn-forest inline-flex items-center justify-center gap-2 text-sm" data-testid="new-chat-button">
            <Plus className="w-4 h-4" /> New chat
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {sessions.map((s) => (
            <button key={s.session_id} onClick={() => pickSession(s.session_id)}
              data-testid={`session-${s.session_id}`}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                s.session_id === sessionId ? "bg-forest/8 text-forest" : "hover:bg-paper text-ink-light"
              }`}>
              <div className="flex items-center gap-2">
                <MessageSquare className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
                <span className="truncate">{s.preview || "New chat"}</span>
              </div>
            </button>
          ))}
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <div className="border-b border-border bg-white px-6 py-4 flex items-center justify-between">
          <div>
            <div className="overline mb-1">AI Advisor</div>
            <h1 className="font-heading text-2xl font-medium tracking-tight">Ask anything about your business.</h1>
          </div>
          <div className="flex bg-paper rounded-lg p-1 border border-border" data-testid="model-toggle">
            {MODELS.map((m) => (
              <button key={m.v} onClick={() => setModel(m.v)}
                data-testid={`model-${m.v}`}
                className={`px-3.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  model === m.v ? "bg-white text-forest shadow-sm" : "text-ink-light"
                }`}>
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-8" data-testid="chat-messages">
          <div className="max-w-3xl mx-auto space-y-6">
            {messages.length === 0 ? (
              <div className="py-20 text-center">
                <div className="w-12 h-12 rounded-xl bg-forest/8 text-forest flex items-center justify-center mx-auto mb-6">
                  <Sparkles className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <h2 className="font-heading text-3xl font-medium tracking-tight mb-3">How can I help today?</h2>
                <p className="text-ink-light mb-10">I know your transactions, debts, and tax profile. Ask away.</p>
                <div className="grid sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
                  {SUGGESTIONS.map((s, i) => (
                    <button key={i} onClick={() => send(s, "suggestion-panel")}
                      data-testid={`suggestion-${i}`}
                      className="text-left p-4 card-surface card-hover text-sm text-ink-light hover:text-ink">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : messages.map((m) => (
              <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`message-${m.role}`}>
                {m.role === "assistant" && (
                  <div className="w-8 h-8 rounded-md bg-forest text-white flex items-center justify-center shrink-0 mt-0.5">
                    <Sparkles className="w-4 h-4" strokeWidth={1.75} />
                  </div>
                )}
                <div className={`max-w-2xl ${m.role === "user" ? "bg-forest/8 text-ink rounded-2xl px-4 py-2.5" : ""}`}>
                  <div className="whitespace-pre-wrap text-[15px] leading-relaxed">{m.content || (streaming && m === messages[messages.length - 1] ? "▋" : "")}</div>
                  {m.role === "assistant" && m.content && !streaming && (
                    <button
                      onClick={() => savePlan(m.id)}
                      disabled={savingPlanFor === m.id}
                      data-testid={`save-plan-${m.id}`}
                      className="mt-3 inline-flex items-center gap-1.5 text-xs text-forest font-medium hover:underline disabled:opacity-60"
                    >
                      {savingPlanFor === m.id
                        ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving plan…</>
                        : <><ClipboardList className="w-3 h-3" /> Save as my 90-day plan</>}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-border bg-white px-6 py-4">
          <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="max-w-3xl mx-auto flex items-end gap-3" data-testid="chat-input-form">
            <textarea
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }}
              placeholder="Ask about taxes, debt, runway… (Enter to send, Shift+Enter for newline)"
              data-testid="chat-input"
              className="flex-1 bg-paper border border-border rounded-xl px-4 py-3 resize-none outline-none focus:ring-2 focus:ring-forest/20 focus:border-forest text-[15px] max-h-40"
            />
            <button type="submit" disabled={streaming || !input.trim() || !sessionId}
              data-testid="send-button"
              className="btn-forest h-12 px-5 inline-flex items-center gap-2 disabled:opacity-50">
              <Send className="w-4 h-4" /> Send
            </button>
          </form>
          <div className="max-w-3xl mx-auto mt-2 text-xs text-ink-lighter text-center">
            Currently chatting with <span className="font-medium text-ink">{MODELS.find((m) => m.v === model)?.label}</span>. Not a substitute for a licensed CPA.
          </div>
        </div>
      </div>
    </div>
  );
}
